/**
 * AI Features API module
 * Covers: Lab Summary, Clinical Summary, Patient History, Billing Assistant,
 *         Staff Chat, and Clinic AI Settings.
 */
import { fetchApi , RateLimitError } from '../client';

// ─────────────────────── Shared types ────────────────────────────────────────

export type AiApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface AiFeatureConfig {
  id: string;
  clinicId: string;
  featureKey: string;
  enabled: boolean;
  /** Monthly request limit for this feature. 0 = the system default (500), as the server applies it. */
  maxRequestsPerMonth: number;
  /**
   * Requests used this month. GET /api/clinic/ai-settings does not send this today (it returns the
   * ai_feature_config row only); show usage only when it is present.
   */
  requestsThisMonth?: number;
  updatedAt: string | null;
}

// ─────────────────────── Lab Summary ─────────────────────────────────────────

export interface LabSummaryResult {
  id: string | null;
  auditLogId: string | null;
  labOrderId: string;
  patientId: string;
  doctorSummary: string | null;
  patientSummary: string | null;
  // Named to match the wire format. The backend DTO fields are `abnormalFindings` /
  // `criticalFlags` (JSON array strings); the previous `…Json` names never existed on the
  // response, so any code reading them silently got undefined.
  abnormalFindings: string | null;
  criticalFlags: string | null;
  approvalStatus: AiApprovalStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  editedDoctorSummary: string | null;
  createdAt: string | null;
  /**
   * Model self-reported confidence, 0–100. `null` means the model reported nothing usable — NOT
   * zero confidence. Render nothing for null; "0%" would read as a confident negative judgement.
   */
  confidence: number | null;
  /** AI-suggested clinician follow-up. Advisory, behind the same sign-off gate. */
  followUp: string | null;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export type LabReviewStatus = 'PENDING_REVIEW' | 'AI_REVIEWED' | 'FLAGGED' | 'FINALIZED';

/** One row of the doctor's lab review worklist. */
export interface LabReviewListItem {
  labOrderId: string;
  patientId: string;
  patientName: string | null;
  /** Decrypted DOB — shown instead of an identifier; clinicians disambiguate by DOB. */
  dateOfBirth: string | null;
  /** Groups tests ordered together (null for legacy rows that never went through the specimen pipeline). */
  accessionId?: string | null;
  appointmentId?: string | null;
  panel: string | null;
  orderedAt: string | null;
  /** null when no AI summary has been generated for this order yet. */
  summaryId: string | null;
  confidence: number | null;
  reviewStatus: LabReviewStatus;
  /** The lab has released this result (it can be reviewed). */
  labReleased?: boolean;
  /** The doctor reviewed it and the patient can see it (medopd-docs 104). */
  sharedWithPatient?: boolean;
  reviewedAt?: string | null;
}

/**
 * One requisition: every test ordered together in the same appointment (same accession). The doctor reviews
 * one record per visit, with the tests inside it. `reviewStatus` is the most urgent of the tests' statuses.
 */
export interface LabReviewGroup {
  groupId: string;
  accessionId: string | null;
  appointmentId: string | null;
  patientId: string;
  patientName: string | null;
  dateOfBirth: string | null;
  orderedAt: string | null;
  reviewStatus: LabReviewStatus;
  testCount: number;
  tests: LabReviewListItem[];
}

/** Wraps a single worklist item as a one-test requisition (used by the in-consultation dialog). */
export function toReviewGroup(item: LabReviewListItem): LabReviewGroup {
  return {
    groupId: item.accessionId ? `acc:${item.accessionId}` : `order:${item.labOrderId}`,
    accessionId: item.accessionId ?? null,
    appointmentId: item.appointmentId ?? null,
    patientId: item.patientId,
    patientName: item.patientName,
    dateOfBirth: item.dateOfBirth,
    orderedAt: item.orderedAt,
    reviewStatus: item.reviewStatus,
    testCount: 1,
    tests: [item],
  };
}

/** One page of the doctor's worklist (GET /ai/lab-summary/my-worklist/page). */
export interface LabReviewWorklistPage {
  items: LabReviewGroup[];
  /** Rows matching the current filters. */
  total: number;
  page: number;
  size: number;
  /** Header counts over the whole worklist, independent of filters. */
  flaggedCount: number;
  awaitingCount: number;
  allCount: number;
}

export interface LabReviewWorklistQuery {
  page?: number;
  size?: number;
  q?: string;
  status?: LabReviewStatus | '';
  patientId?: string;
}

export const aiLabSummary = {
  /** Paged + filterable worklist for the review screen. Same server-side doctor+clinic scoping as myWorklist. */
  myWorklistPage: ({ page = 0, size = 12, q, status, patientId }: LabReviewWorklistQuery = {}) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (patientId) params.set('patientId', patientId);
    return fetchApi<LabReviewWorklistPage>(`/api/ai/lab-summary/my-worklist/page?${params.toString()}`);
  },

  /**
   * The signed-in doctor's own review worklist. Scoped server-side from the principal.
   *
   * `patientId` only NARROWS that scope (used by the in-consultation popup) — it is applied on top
   * of the doctor+clinic filter server-side, so it can never surface another doctor's or another
   * clinic's results.
   */
  myWorklist: (patientId?: string) =>
    fetchApi<LabReviewListItem[]>(
      `/api/ai/lab-summary/my-worklist${patientId ? `?patientId=${encodeURIComponent(patientId)}` : ''}`,
    ),

  generate: (labOrderId: string, forceRefresh = false) =>
    fetchApi<LabSummaryResult>(`/api/ai/lab-summary/${labOrderId}/generate?forceRefresh=${forceRefresh}`, {
      method: 'POST',
    }),

  approve: (summaryId: string, editedDoctorSummary?: string) =>
    fetchApi<LabSummaryResult>(`/api/ai/lab-summary/${summaryId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ editedDoctorSummary }),
    }),

  reject: (summaryId: string, reason: string) =>
    fetchApi<void>(`/api/ai/lab-summary/${summaryId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// ─────────────────────── Clinical Summary ────────────────────────────────────

export interface ClinicalSummaryResult {
  id: string | null;
  auditLogId: string | null;
  visitId: string;
  patientId: string;
  diagnosisSummary: string | null;
  medicationsPrescribedJson: string | null;
  labOrdersSummaryJson: string | null;
  followUpPlan: string | null;
  clinicalNotes: string | null;
  approvalStatus: AiApprovalStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  approvedVersion: string | null;
  editCount: number;
  createdAt: string | null;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export const aiClinicalSummary = {
  generate: (visitId: string, forceRefresh = false) =>
    fetchApi<ClinicalSummaryResult>(`/api/ai/clinical-summary/${visitId}/generate?forceRefresh=${forceRefresh}`, {
      method: 'POST',
    }),

  approve: (summaryId: string, edits?: { diagnosisSummary?: string; followUpPlan?: string; clinicalNotes?: string }) =>
    fetchApi<ClinicalSummaryResult>(`/api/ai/clinical-summary/${summaryId}/approve`, {
      method: 'POST',
      body: JSON.stringify(edits ?? {}),
    }),

  reject: (summaryId: string, reason: string) =>
    fetchApi<void>(`/api/ai/clinical-summary/${summaryId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// ─────────────────────── Clinical Documentation Assistant ────────────────────

export interface SuggestedInvestigation {
  investigation: string;
  rationale: string;
}

export interface DocumentationResult {
  id: string | null;
  auditLogId: string | null;
  patientId: string;
  appointmentId: string | null;
  clinicalSummary: string | null;
  suggestedClinicalNote: string | null;
  suggestedAuditReason: string | null;
  suggestedAuditDetails: string | null;
  /** JSON array of abnormal-finding strings */
  abnormalFindingsJson: string | null;
  /** JSON array of { investigation, rationale } */
  suggestedInvestigationsJson: string | null;
  approvalStatus: AiApprovalStatus;
  editCount: number;
  createdAt: string | null;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export const aiClinicalDocumentation = {
  generate: (patientId: string, appointmentId?: string, forceRefresh = false) => {
    const qs = new URLSearchParams();
    if (appointmentId) qs.set('appointmentId', appointmentId);
    qs.set('forceRefresh', String(forceRefresh));
    return fetchApi<DocumentationResult>(
      `/api/ai/clinical-documentation/${patientId}/generate?${qs.toString()}`,
      { method: 'POST' }
    );
  },

  approve: (docId: string, edits?: {
    clinicalSummary?: string; suggestedNote?: string;
    suggestedAuditReason?: string; suggestedAuditDetails?: string;
  }) =>
    fetchApi<DocumentationResult>(`/api/ai/clinical-documentation/${docId}/approve`, {
      method: 'POST',
      body: JSON.stringify(edits ?? {}),
    }),

  reject: (docId: string, reason: string) =>
    fetchApi<void>(`/api/ai/clinical-documentation/${docId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// ─────────────────────── Diagnosis Medication Templates ──────────────────────

/** One suggested medication line for a diagnosis (approved template or AI). */
export interface DiagnosisMedicationLine {
  name: string;
  dosage: string | null;
  frequency: string | null;
  duration: string | null;
  instructions: string | null;
  rationale: string | null;
  /** medications_master id when the name resolved to the approved formulary. */
  medicationMasterId: string | null;
  /** True when the line matched an entry in medications_master. */
  masterMatched: boolean;
}

export interface DiagnosisSuggestionResult {
  /** 'APPROVED_TEMPLATE' (instant, deterministic) or 'AI' (review required). */
  source: 'APPROVED_TEMPLATE' | 'AI';
  templateId: string | null;
  diagnosis: string;
  aiRequestLogId: string | null;
  medications: DiagnosisMedicationLine[];
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export interface DiagnosisApprovePayload {
  diagnosis: string;
  medications: DiagnosisMedicationLine[];
  aiRequestLogId?: string | null;
  aiModelVersion?: string | null;
  aiPromptVersion?: string | null;
}

export const aiDiagnosisTemplates = {
  /** Free/deterministic-only check — never falls through to AI. Used for proactive/real-time
   *  auto-suggest as the diagnosis field stabilizes, so debounced auto-firing never spends AI quota. */
  check: (diagnosis: string) =>
    fetchApi<DiagnosisSuggestionResult>(`/api/ai/diagnosis-templates/check?diagnosis=${encodeURIComponent(diagnosis)}`),

  /**
   * Suggest medications for a diagnosis: approved global template first (no consent needed — no
   * PHI leaves the system), AI fallback otherwise (requires visitId + consentAcknowledged — DPDP/
   * ABDM per-visit AI-suggestion consent; a "consent required" errorMessage means the caller should
   * show the consent dialog and retry with consentAcknowledged=true).
   */
  suggest: (diagnosis: string, patientId?: string, visitId?: string, consentAcknowledged?: boolean) => {
    const qs = new URLSearchParams({ diagnosis });
    if (patientId) qs.set('patientId', patientId);
    if (visitId) qs.set('visitId', visitId);
    if (consentAcknowledged) qs.set('consentAcknowledged', 'true');
    return fetchApi<DiagnosisSuggestionResult>(
      `/api/ai/diagnosis-templates/suggest?${qs.toString()}`
    );
  },

  /** Cache a physician-approved set as a new APPROVED global template version. */
  approve: (payload: DiagnosisApprovePayload) =>
    fetchApi<DiagnosisSuggestionResult>('/api/ai/diagnosis-templates/approve', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ─────────────────────── Symptom → Differential Diagnosis ────────────────────

/** One ranked differential-diagnosis suggestion from a chief complaint / symptoms. */
export interface DiagnosisSuggestion {
  diagnosis: string;
  icd10Code: string | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  rationale: string | null;
  /** AI answers: the code is verified against the ICD-10 table (MODEL) or found from the name (SEARCH). */
  icd10Description?: string | null;
  codeSource?: 'MODEL' | 'SEARCH' | null;
  /** Facts the suggestion relies on, cited as given (test name, relative date). */
  supportingFacts?: string[] | null;
}

/** A test to consider next, matched to the clinic's lab tests when possible (medopd-docs 154, phase 5b). */
export interface DifferentialNextTest {
  name: string;
  reason: string | null;
  testId: string | null;
  matched: boolean;
}

export interface SymptomDiagnosisResult {
  /** 'APPROVED_TEMPLATE' (instant, deterministic) or 'AI' (review required). */
  source?: 'APPROVED_TEMPLATE' | 'AI' | null;
  templateId?: string | null;
  chiefComplaint: string;
  aiRequestLogId: string | null;
  suggestions: DiagnosisSuggestion[];
  /** True for a model answer (shown as AI-generated). */
  aiGenerated?: boolean;
  redFlags?: string[] | null;
  nextTests?: DifferentialNextTest[] | null;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export interface DifferentialApprovePayload {
  chiefComplaint: string;
  suggestions: DiagnosisSuggestion[];
  aiRequestLogId?: string | null;
  aiModelVersion?: string | null;
  aiPromptVersion?: string | null;
}

export const aiSymptomDiagnosis = {
  /** Free/deterministic-only check — never falls through to AI. Mirrors aiDiagnosisTemplates.check. */
  check: (chiefComplaint: string) =>
    fetchApi<SymptomDiagnosisResult>(`/api/ai/differential-diagnosis/check?chiefComplaint=${encodeURIComponent(chiefComplaint)}`),

  /**
   * Suggest a ranked differential diagnosis from the chief complaint — complements
   * aiDiagnosisTemplates, which suggests medications FROM a diagnosis. Physician-review only,
   * never auto-fills the diagnosis field. Approved global template first (no consent needed — no
   * PHI leaves the system), RAG-grounded AI fallback otherwise (requires visitId +
   * consentAcknowledged — DPDP/ABDM per-visit AI-suggestion consent).
   */
  suggest: (chiefComplaint: string, patientId?: string, visitId?: string, consentAcknowledged?: boolean) => {
    const qs = new URLSearchParams({ chiefComplaint });
    if (patientId) qs.set('patientId', patientId);
    if (visitId) qs.set('visitId', visitId);
    if (consentAcknowledged) qs.set('consentAcknowledged', 'true');
    return fetchApi<SymptomDiagnosisResult>(`/api/ai/differential-diagnosis/suggest?${qs.toString()}`);
  },

  /** Cache a physician-approved differential as a new APPROVED global template version. */
  approve: (payload: DifferentialApprovePayload) =>
    fetchApi<SymptomDiagnosisResult>('/api/ai/differential-diagnosis/approve', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ─────────────────────── Patient History ─────────────────────────────────────

export interface PatientHistoryResult {
  id: string | null;
  auditLogId: string | null;
  patientId: string;
  summaryJson: string | null;
  createdAt: string | null;
  expiresAt: string | null;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export interface PatientHistorySummary {
  age: number | null;
  gender: string | null;
  bloodGroup: string | null;
  allergies: string[];
  chronicConditions: string[];
  recentVisits: Array<{
    date: string;
    chiefComplaint: string;
    diagnosis: string;
  }>;
  currentMedications: Array<{
    name: string;
    dosage: string;
    frequency: string;
  }>;
  vitalsSnapshot: {
    bp: string | null;
    pulse: number | null;
    weight: number | null;
  } | null;
  labFindings?: string[];
  pendingLabOrders?: string[];
}

export const aiPatientHistory = {
  getOrGenerate: (patientId: string, forceRefresh = false) =>
    fetchApi<PatientHistoryResult>(`/api/ai/patient-history/${patientId}?forceRefresh=${forceRefresh}`),
};

// ─────────────────────── Billing Assistant ───────────────────────────────────

export interface MissingCharge {
  description: string;
  possibleCode: string | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface BillingAssistantResult {
  id: string | null;
  auditLogId: string | null;
  billId: string;
  patientId: string;
  missingChargesJson: string | null;
  patientExplanation: string | null;
  claimNote: string | null;
  approvalStatus: AiApprovalStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  doctorApprovalRequired: boolean;
  createdAt: string | null;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export const aiBillingAssistant = {
  analyze: (billId: string, forceRefresh = false) =>
    fetchApi<BillingAssistantResult>(`/api/ai/billing/${billId}/analyze?forceRefresh=${forceRefresh}`, {
      method: 'POST',
    }),

  approve: (summaryId: string) =>
    fetchApi<BillingAssistantResult>(`/api/ai/billing/${summaryId}/approve`, { method: 'POST' }),

  reject: (summaryId: string, reason: string) =>
    fetchApi<void>(`/api/ai/billing/${summaryId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

// ─────────────────────── Staff Chat ──────────────────────────────────────────

export interface AiChatMessage {
  id: string;
  sessionId: string;
  clinicId: string;
  userId: string;
  messageRole: 'USER' | 'ASSISTANT' | 'TOOL_CALL' | 'TOOL_RESULT';
  content: string;
  aiRequestLogId: string | null;
  createdAt: string;
}

export interface ChatResponse {
  sessionId: string | null;
  messageId: string | null;
  response: string;
  disclaimer: string;
  success: boolean;
  errorMessage: string | null;
}

export const aiStaffChat = {
  sendMessage: (message: string, patientId?: string) =>
    fetchApi<ChatResponse>('/api/ai/chat/message', {
      method: 'POST',
      body: JSON.stringify(patientId ? { message, patientId } : { message }),
    }),

  getHistory: (sessionId: string) =>
    fetchApi<AiChatMessage[]>(`/api/ai/chat/sessions/${sessionId}/history`),

  endSession: (sessionId: string) =>
    fetchApi<void>(`/api/ai/chat/sessions/${sessionId}/end`, { method: 'POST' }),
};

export interface PrescribingReference { title: string; content: string; }
export interface PrescribingReferenceResponse {
  drug: string;
  references: PrescribingReference[];
  patientFlags: string[];
}

/** RAG-backed prescription-writing assist: dosing/interaction references + patient-specific safety flags. */
export const aiRag = {
  prescribingReference: (drug: string, patientId?: string) => {
    const qs = new URLSearchParams({ drug });
    if (patientId) qs.set('patientId', patientId);
    return fetchApi<PrescribingReferenceResponse>(`/api/ai/rag/prescribing-reference?${qs.toString()}`);
  },
};

// ─────────────────────── Clinic AI Settings ──────────────────────────────────

/** Every AiFeatureKey the backend knows (entity/AiFeatureKey.java) — the admin screens' dropdowns read this. */
export const AI_FEATURE_KEYS = [
  'PRESCRIPTION_SAFETY_CHECK', 'LAB_REPORT_SUMMARY', 'CLINICAL_VISIT_SUMMARY', 'PATIENT_HISTORY_SUMMARY',
  'BILLING_ASSISTANT', 'STAFF_CHAT', 'FOLLOW_UP_RECOMMENDATION', 'TEMPLATE_RECOMMENDATION',
  'DIFFERENTIAL_DIAGNOSIS_SUGGESTION', 'CLINICAL_DOCUMENTATION', 'CONSULTATION_SCRIBE', 'INPATIENT_ROUND_SCRIBE',
  'NEXT_BEST_ACTION', 'CAMPAIGN_COPY', 'INPATIENT_SNAPSHOT', 'DISCHARGE_SUMMARY_DRAFT', 'REVENUE_SUGGESTIONS',
  'DRUG_INTERACTION_INGESTION', 'CHECKIN_SUMMARY', 'COPILOT', 'PATIENT_ASSISTANT',
] as const;

/** What a panel shows when a call throws: a rate limit says when to retry, anything else stays generic. */
export function aiErrorText(e: unknown): string {
  if (e instanceof RateLimitError) return `The AI service is busy. Try again in ${e.retryAfterSeconds} seconds.`;
  if (e instanceof Error && e.message) return e.message;
  return 'Failed to reach AI service.';
}

/** Operational status of the AI layer, including which provider answers (deterministic templates or a model). */
export const aiStatus = () => fetchApi<{ provider?: string; deterministicMode?: boolean; version?: string }>('/api/ai/status');

export const clinicAiSettings = {
  listSettings: () =>
    fetchApi<AiFeatureConfig[]>('/api/clinic/ai-settings'),

  getFeatureFlags: () =>
    fetchApi<Record<string, boolean>>('/api/clinic/ai-settings/flags'),

  enableFeature: (featureKey: string) =>
    fetchApi<AiFeatureConfig>(`/api/clinic/ai-settings/${featureKey}/enable`, { method: 'POST' }),

  disableFeature: (featureKey: string, reason: string) =>
    fetchApi<AiFeatureConfig>(`/api/clinic/ai-settings/${featureKey}/disable`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  updateQuota: (featureKey: string, quotaLimit: number) =>
    fetchApi<AiFeatureConfig>(`/api/clinic/ai-settings/${featureKey}/quota`, {
      method: 'PUT',
      body: JSON.stringify({ quotaLimit }),
    }),
};
